/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import * as Yup from 'yup';
import Upload, { uploadSchema } from './upload.js';

export const flowCellSchema = uploadSchema.shape({
  isConfigurable: Yup.boolean().default(false).required('isConfigurable is required'),
  configurableParameters: Yup.object().shape({
    type: Yup.string().oneOf(['flowCell']).required('Type is required'),
    portToPortDistance: Yup.number().nullable(),
    channelWidth: Yup.number().nullable(),
    channelDepth: Yup.number().nullable(),
    taperAngle: Yup.number().nullable(),
    cornerRadius: Yup.number().nullable(),
    viaDiameter: Yup.number().nullable(),
    component: Yup.string().nullable(),
  }).required('Configurable parameters are required'),
});

/**
 * @typedef {Yup.InferType<typeof flowCellSchema>} FlowCellSchema
 * @typedef {BaseDocData & FlowCellSchema} FlowCellDocData
 */

/**
 * Class representing a configurable Flow Cell upload.
 * @class
 * @extends Upload
 */
export default class FlowCell extends Upload {
  /** @type {FlowCellDocData['isConfigurable']} */ isConfigurable;
  /** @type {FlowCellDocData['configurableParameters']} */ configurableParameters;

  /**
   * Create a {@link FlowCell}.
   * @param {FlowCellDocData} param0 - A {@link FlowCellDocData} object.
   */
  constructor({ isConfigurable, configurableParameters, ...props }) {
    const validatedData = flowCellSchema.validateSync({
      isConfigurable,
      configurableParameters,
      ...props,
    });

    super(validatedData);
    this.isConfigurable = validatedData.isConfigurable;
    this.configurableParameters = validatedData.configurableParameters;
  }

  /**
   * Converts the {@link FlowCell} instance to a Firestore data object.
   * @returns {Omit<FlowCellDocData, 'id'>} {@link FlowCellDocData} (without `id`).
   */
  toFirestore() {
    const baseDoc = super.toFirestore();
    return {
      ...baseDoc,
      isConfigurable: this.isConfigurable,
      configurableParameters: this.configurableParameters,
    };
  }

  /**
   * Creates a {@link FlowCell} instance from Firestore data.
   * @param {any} docData - Raw Firestore document.
   * @returns {FlowCell} A {@link FlowCell} instance.
   */
  static fromFirestore(docData) {
    return new FlowCell(docData);
  }
}
